import React, { useLayoutEffect, useRef } from "react";
import AnimatedWords from './AnimatedWords';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

const Facts = () => {
  const containerRef = useRef(null);

  useLayoutEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        defaults: { ease: 'power2.out' },
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top 75%',
          toggleActions: 'play none none none',
          once: true
        }
      });

      const headingWords = containerRef.current?.querySelectorAll('.facts-head h2 [data-animated-word]');
      if (headingWords && headingWords.length) {
        tl.fromTo(headingWords, { yPercent: 100 }, { yPercent: 0, duration: 0.5, ease: 'power3.out', stagger: 0.06, clearProps: 'transform' }, 0);
      }

      // Count-up for statistics, tied to timeline
      const dds = containerRef.current?.querySelectorAll('.fact dd');
      const startCount = (dd) => {
        const raw = (dd.textContent || '').trim();
        const slashMatch = raw.match(/^(?<prefix>[+]?)(?<num>\d+(?:\.\d+)?)(?<rest>\/\d+.*)$/);
        const match = raw.match(/^(?<prefix>[+]?)(?<num>\d+(?:\.\d+)?)(?<rest>.*)$/);

        let prefix = '', targetNum = 0, rest = '', decimals = 0;
        if (slashMatch) {
          prefix = slashMatch.groups?.prefix || '';
          targetNum = parseFloat(slashMatch.groups?.num || '0');
          rest = slashMatch.groups?.rest || '';
          decimals = (slashMatch.groups?.num || '').includes('.') ? (slashMatch.groups?.num || '').split('.')[1].length : 0;
        } else if (match) {
          prefix = match.groups?.prefix || '';
          targetNum = parseFloat(match.groups?.num || '0');
          rest = match.groups?.rest || '';
          decimals = (match.groups?.num || '').includes('.') ? (match.groups?.num || '').split('.')[1].length : 0;
        } else {
          return;
        }

        const isK = /k/i.test(rest);

        // Initialize to 0 display
        dd.textContent = `${prefix}${decimals > 0 ? (0).toFixed(decimals) : '0'}${rest}`;

        const counter = { val: 0 };
        gsap.to(counter, {
          val: targetNum,
          duration: 1.5,
          ease: 'power1.out',
          onUpdate: () => {
            let displayVal = decimals > 0 ? parseFloat(counter.val.toFixed(decimals)) : Math.round(counter.val);
            const numStr = isK ? String(displayVal) : String(displayVal);
            dd.textContent = `${prefix}${numStr}${rest}`;
          }
        });
      };

      if (dds && dds.length) {
        tl.add(() => {
          dds.forEach((dd) => startCount(dd));
        }, '>-0.1');
      }
    }, containerRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="facts"
      className="relative p-10 bg-cover bg-center bg-no-repeat text-white min-h-screen flex items-center"
      style={{ backgroundImage: "url('/images/quick_facts.webp')", backgroundColor: '#0a0a0a' }}
      ref={containerRef}
    >
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/60"></div>

      {/* Content as a 2x2 grid */}
      <div className="relative container mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 grid-rows-2 gap-8 w-full">
        {/* 1st cell - Delivering Reliability text */}
        <div className="facts-head flex flex-col items-center md:items-start max-w-lg mx-auto text-center md:text-left">
          <div className="container section-badge flex items-center gap-3 text-[16px] md:text-[20px] !px-1">
            <div className="badge-dot"></div>
            Quick Facts
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold mt-2 leading-tight inline-block mx-auto md:mx-0">
            <AnimatedWords auto={false} lines={["Delivering Reliability", "Across Every Mile"]} accentMap={[[], [0, 1, 2]]} />
          </h2>
        </div>

        {/* 2nd cell - empty (spacer) -> hidden on mobile to stack stats below text */}
        <div className="hidden md:block"></div>

        {/* 3rd cell - empty (spacer) -> hidden on mobile */}
        <div className="hidden md:block"></div>

        {/* 4th cell - statistics */}
        <dl className="grid grid-cols-2 gap-x-8 gap-y-8 place-items-center md:place-items-start text-center md:text-left mx-auto md:mx-0">
          <div className="fact">
            <dd className="text-5xl md:text-6xl lg:text-7xl font-semibold">99.4%</dd>
            <dt className="text-base md:text-lg lg:text-xl mt-1 text-gray-200 font-semibold">On-Time Dispatch Rate</dt>
          </div>
          <div className="fact">
            <dd className="text-5xl md:text-6xl lg:text-7xl font-semibold">24/7</dd>
            <dt className="text-base md:text-lg lg:text-xl mt-1 text-gray-200 font-semibold">Live Tracking & Support</dt>
          </div>
          <div className="fact">
            <dd className="text-5xl md:text-6xl lg:text-7xl font-semibold">500+</dd>
            <dt className="text-base md:text-lg lg:text-xl mt-1 text-gray-200 font-semibold">Cities & Industrial Hubs Connected</dt>
          </div>
          <div className="fact">
            <dd className="text-5xl md:text-6xl lg:text-7xl font-semibold">3,500+</dd>
            <dt className="text-base md:text-lg lg:text-xl mt-1 text-gray-200 font-semibold">Monthly Deliveries Completed</dt>
          </div>
        </dl>
      </div>
    </section>
  );
};

export default Facts;
